import {
  discriminate,
  GoalExportDto,
  GoalOutcomeOptions,
  GoalReviewStatusOptions,
  GoalTypeOptions,
  UserField,
  UserFieldType,
  UserFilter,
} from "@coaching-culture/types";
import {
  Box,
  Button,
  DatePill,
  Flex,
  IconWrap,
  Label,
  Loader,
  Panel,
  PanelHeader,
  ProgressBar,
  SortableTable,
  SortableTableColumn,
  Text,
  UserFlag,
} from "@coaching-culture/ui";
import { useGoalTerminology } from "auth/OrgProvider";
import axios from "axios";
import CenterColumn from "components/CenterColumn";
import { FilterModal } from "components/FilterPanel";
import { PageHeader } from "components/PageHeader";
import qsStringify from "qs-stringify";
import { useUserFields } from "queries/users";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";
import {
  FaBullseye,
  FaChartLine,
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaMinusCircle,
  FaPauseCircle,
  FaRedo,
  FaRegClock,
  FaSeedling,
  FaSmile,
  FaThumbsUp,
  FaTimesCircle,
  FaTrophy,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { saveExportAsCsv } from "utils";
import { isOverdue } from "utils/content";
import { formatDate } from "utils/dates";

const statusConfig: Record<
  GoalReviewStatusOptions,
  { text: string; color: string; icon: IconType }
> = {
  notstarted: { text: "Not Started", color: "grey", icon: FaRegClock },
  inprogress: { text: "In Progress", color: "warning", icon: FaSmile },
  achieved: { text: "Achieved", color: "positive", icon: FaThumbsUp },
  onhold: { text: "On Hold", color: "grey", icon: FaPauseCircle },
  blocked: { text: "Blocked", color: "danger", icon: FaExclamationCircle },
};

const outcomeConfig: Record<
  GoalOutcomeOptions,
  { text: string; color: string; icon: IconType }
> = {
  completedachieved: {
    text: "Achieved",
    color: "positive",
    icon: FaCheckCircle,
  },
  exceeded: { text: "Exceeded", color: "positive", icon: FaTrophy },
  partially: { text: "Partially Met", color: "warning", icon: FaMinusCircle },
  missed: { text: "Missed", color: "danger", icon: FaTimesCircle },
  nla: { text: "No Longer Applicable", color: "grey", icon: FaMinusCircle },
  open: { text: "Reopened", color: "warning", icon: FaRedo },
};

const typeConfig: Record<
  GoalTypeOptions,
  { text: string; color: string; icon: IconType }
> = {
  performance: { text: "Performance", color: "blue", icon: FaChartLine },
  development: { text: "Development", color: "secondary", icon: FaSeedling },
};

const pillStyle = { whiteSpace: "nowrap" } as const;

const StatusPill = ({ status }: { status: GoalReviewStatusOptions }) => {
  const cfg = statusConfig[status];

  if (cfg == null) {
    return null;
  }

  return (
    <Label size="small" color={cfg.color} icon={cfg.icon} style={pillStyle}>
      {cfg.text}
    </Label>
  );
};

const OutcomePill = ({ outcome }: { outcome: GoalOutcomeOptions | null }) => {
  const cfg = outcome == null ? null : outcomeConfig[outcome];

  if (cfg == null) {
    return (
      <Text as="span" color="grey2">
        &mdash;
      </Text>
    );
  }

  return (
    <Label size="small" color={cfg.color} icon={cfg.icon} style={pillStyle}>
      {cfg.text}
    </Label>
  );
};

const GoalProgress = ({ goal }: { goal: GoalExportDto }) => {
  if (goal.target == null || goal.progress == null) {
    return (
      <Text as="span" color="grey2">
        &mdash;
      </Text>
    );
  }

  const suffix = goal.measureType === "percentage" ? "%" : "";
  const fraction =
    goal.target === 0 ? 0 : Math.min(1, goal.progress / goal.target);

  return (
    <Box minWidth={90}>
      <Text fontSize={2} fontWeight={500} mb={1}>
        {goal.progress}
        {suffix} / {goal.target}
        {suffix}
      </Text>
      <ProgressBar
        value={fraction}
        color={fraction >= 1 ? "positive" : "primary"}
      />
    </Box>
  );
};

export const GoalExport = () => {
  const { data: userFields, isFetched } = useUserFields();
  const [data, setData] = useState<GoalExportDto[]>(null);
  const goalTerms = useGoalTerminology();
  const [filter, setFilter] = useState<UserFilter>({
    groups: [],
    userFields: [],
  });
  const [showFilters, setShowFilters] = useState(false);

  const selectFields = (userFields || []).filter(
    discriminate("type", UserFieldType.Select),
  );

  useEffect(() => {
    setData(null);
    axios
      .get(`/api/v2/goals/export?${qsStringify({ filter } as any)}`)
      .then(({ data }: { data: GoalExportDto[] }) => {
        setData(data);
      });
  }, [filter]);

  const formatUserField = useCallback(
    (
      user: {
        id: string;
        value: string | number;
      }[],
      userFieldDef: UserField,
    ) => {
      const field = user?.find((uf) => uf.id === userFieldDef.id);
      if (field != null) {
        if (userFieldDef.type === UserFieldType.Select) {
          const selectField = selectFields.find(
            (f) => f.id === userFieldDef.id,
          );
          return selectField.options.find((option) => option.id === field.value)
            ?.name;
        } else {
          return field.value as string;
        }
      }
      return "";
    },
    [selectFields],
  );

  const getUserFieldCols = useMemo<SortableTableColumn<GoalExportDto>[]>(() => {
    return (
      userFields?.map((y) => ({
        label: userFields?.find((ufo) => ufo.id === y.id).name,
        name: y.id ?? "",
        sortFormat: (x: GoalExportDto) => {
          return formatUserField(x.userFields, y);
        },
        format: (x: GoalExportDto) => {
          return formatUserField(x.userFields, y);
        },
        exportFormat: (x: GoalExportDto) => {
          return formatUserField(x.userFields, y);
        },
      })) ?? []
    );
  }, [userFields, formatUserField]);

  const cols = useMemo((): SortableTableColumn<GoalExportDto>[] => {
    const cols: SortableTableColumn<GoalExportDto>[] = [
      {
        name: "name",
        label: "User",
        exportLabel: "Name",
        format: (x) => (
          <UserFlag
            user={{
              id: "",
              name: x.userName,
              email: x.userEmail,
              profileImage: null,
              isDeactivated: false,
            }}
          />
        ),
        sortFormat: (x) => x.userName.toLowerCase(),
        exportFormat: (x) => x.userName,
      },
      {
        name: "email",
        label: "Email",
        hidden: true,
        exportFormat: (x) => x.userEmail,
      },
      {
        name: "managers",
        label: "Managers",
        format: (x) =>
          x.managers.map((manager, i) => (
            <Link to={`/success/people/users/${manager.id}`}>
              <UserFlag
                mb={i !== x.managers.length - 1 ? 1 : 0}
                user={{
                  id: manager.id,
                  name: manager.name,
                  email: null,
                  profileImage: manager.profileImage,
                  isDeactivated: manager.isDeactivated,
                }}
              />
            </Link>
          )),

        sortFormat: (x) =>
          x.managers.length === 0
            ? ""
            : x.managers.sort()[0].name.toLowerCase(),
        exportFormat: (x) =>
          x.managers.map((manager) => manager.name).toString(),
      },
      {
        name: "goalName",
        label: `${goalTerms.goal.asTitle()} Name`,
        format: (x) => (
          <Text
            fontWeight={x.isObjective ? 400 : 500}
            color={x.isObjective ? "grey1" : "body"}
          >
            {x.isObjective
              ? data?.find((goal) => goal.id === x.parentId).purpose
              : x.purpose}
          </Text>
        ),
        sortFormat: (x) =>
          x.isObjective
            ? data?.find((goal) => goal.id === x.parentId).purpose
            : x.purpose,
        exportFormat: (x) =>
          x.isObjective
            ? data?.find((goal) => goal.id === x.parentId).purpose
            : x.purpose,
      },
      {
        name: "subGoalName",
        label: `${goalTerms.objective.asTitle()} Name`,
        format: (x) =>
          x.isObjective ? <Text fontWeight={500}>{x.purpose}</Text> : "",
        sortFormat: (x) => (x.isObjective ? x.purpose : ""),
        exportFormat: (x) => (x.isObjective ? x.purpose : ""),
      },
      {
        name: "type",
        label: "Type",
        format: (x) => {
          const cfg = typeConfig[x.type];
          return cfg == null ? (
            x.type
          ) : (
            <Label
              size="small"
              color={cfg.color}
              icon={cfg.icon}
              style={pillStyle}
            >
              {cfg.text}
            </Label>
          );
        },
        sortFormat: (x) => x.type,
        exportFormat: (x) => x.type,
      },
      {
        name: "lastUpdated",
        label: "Last updated",
        format: (x) => <DatePill value={x.updatedOn} dateOnly />,
        exportFormat: (x) =>
          x.updatedOn == null
            ? "Never"
            : formatDate(x.updatedOn, "dd/MM/yyyy HH:mm"),
        sortFormat: (x) =>
          x.updatedOn == null ? 0 : new Date(x.updatedOn).getTime(),
      },
      {
        name: "created",
        label: "Start",
        format: (x) => <DatePill value={x.createdOn} dateOnly />,
        exportFormat: (x) => formatDate(x.createdOn, "dd/MM/yyyy HH:mm"),
        sortFormat: (x) => new Date(x.createdOn).getTime(),
      },
      {
        name: "deadline",
        label: "Deadline",
        format: (x) => (
          <DatePill
            value={x.deadline}
            dateOnly
            color={isOverdue(x) ? "danger" : undefined}
          />
        ),
        exportFormat: (x) => formatDate(x.deadline, "dd/MM/yyyy HH:mm"),
        sortFormat: (x) => x.deadline,
      },
      {
        name: "overdue",
        label: "Overdue",
        format: (x) =>
          isOverdue(x) ? (
            <Label
              size="small"
              color="danger"
              icon={FaExclamationTriangle}
              style={pillStyle}
            >
              Overdue
            </Label>
          ) : (
            <Text as="span" color="grey2">
              &mdash;
            </Text>
          ),
        sortFormat: (x) => (isOverdue(x) ? 1 : 0),
        exportFormat: (x) => (isOverdue(x) ? "Yes" : "No"),
      },
      {
        name: "completedOn",
        label: "Completed on",
        format: (x) => <DatePill value={x.completedOn} dateOnly />,
        exportFormat: (x) =>
          x.completedOn == null
            ? "Never"
            : formatDate(x.completedOn, "dd/MM/yyyy HH:mm"),
        sortFormat: (x) =>
          x.completedOn == null ? 0 : new Date(x.completedOn).getTime(),
      },
      {
        name: "status",
        label: "Status",
        format: (x) => <StatusPill status={x.status} />,
        sortFormat: (x) => x.status,
        exportFormat: (x) => x.status,
      },
      {
        name: "outcome",
        label: "Outcome",
        format: (x) => <OutcomePill outcome={x.outcome} />,
        sortFormat: (x) => x.outcome ?? "",
        exportFormat: (x) => x.outcome,
      },
      {
        name: "progressDisplay",
        label: "Progress",
        excludeFromExport: true,
        format: (x) => <GoalProgress goal={x} />,
        sortFormat: (x) =>
          x.target == null || x.progress == null || x.target === 0
            ? -1
            : x.progress / x.target,
      },
      {
        name: "target",
        label: "Target",
        hidden: true,
        exportFormat: (x) => x.target?.toString() ?? "",
      },
      {
        name: "progress",
        label: "Progress",
        hidden: true,
        exportFormat: (x) => x.progress?.toString() ?? "",
      },
      {
        name: "associations",
        label: `${goalTerms.goal.asTitle()} Associations`,
        format: (x) => x.associations.map((x) => x.name).join(" | "),
        exportFormat: (x) => x.associations.map((x) => x.name).join(" | "),
      },
    ];

    return cols.concat(getUserFieldCols);
  }, [data, getUserFieldCols, goalTerms]);

  return (
    <CenterColumn width="100%">
      {!isFetched ? (
        <Loader />
      ) : (
        <>
          <PageHeader
            text={`Export ${goalTerms.goal.asPluralTitle()} / ${goalTerms.objective.asPluralTitle()}`}
            subtitle={`Export and view ${goalTerms.goal.asPluralBody()} / ${goalTerms.objective.asPluralBody()} for all users`}
          >
            <Button onClick={() => setShowFilters(true)}>
              Filters ({filter.groups.length + filter.userFields.length})
            </Button>
          </PageHeader>
          <FilterModal
            isOpen={showFilters}
            filter={filter}
            onClose={(result) => {
              if (result) {
                setFilter(result);
              }
              setShowFilters(false);
            }}
          />
          <Panel>
            <PanelHeader>
              <Flex alignItems="center" flex="1">
                <IconWrap icon={FaBullseye} color="primary" size="2em" />
                <Text fontSize={3} fontWeight={600} ml={2}>
                  {goalTerms.goal.asPluralTitle()} &amp;{" "}
                  {goalTerms.objective.asPluralTitle()}
                </Text>
              </Flex>
              {data != null && (
                <Text fontWeight={500} color="grey1">
                  {data.length} record{data.length === 1 ? "" : "s"}
                </Text>
              )}
            </PanelHeader>
            <SortableTable<GoalExportDto>
              columns={cols}
              data={data}
              defaultSort={0}
              rowKey="id"
              paged
              onExport={saveExportAsCsv("GoalsExport.csv")}
              emptyMessage={`No ${goalTerms.goal.asPluralBody()} found.`}
            />
          </Panel>
        </>
      )}
    </CenterColumn>
  );
};
